import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useBooks } from '@/hooks/useBooks'
import { uploadBook } from '@/lib/api'
import ImportFromGithub from '@/components/ImportFromGithub'
import RepoPreview from '@/components/RepoPreview'
import Logo from '@/components/Logo'

const STATUS_LABEL: Record<string, string> = {
  pending: 'Queued…',
  ingesting: 'Processing…',
  ready: '',
  failed: 'Failed',
}

export default function Home() {
  const { viewer, logout } = useAuth()
  const { data: books, isLoading } = useBooks()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [previewSlug, setPreviewSlug] = useState<string | null>(null)

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      await uploadBook(file, 'lt')
      await queryClient.invalidateQueries({ queryKey: ['books'] })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white sticky top-0">
        <div className="flex items-center gap-2">
          <Logo size={28} className="rounded-lg" />
          <h1 className="text-lg font-semibold text-slate-900">TransliFlow</h1>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/settings" className="text-slate-500 hover:text-slate-800">Settings</Link>
          <button onClick={logout} className="text-slate-500 hover:text-slate-800">Sign out</button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-slate-500">
            Welcome{viewer?.name ? `, ${viewer.name}` : ''}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setImportOpen(true)}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
            >
              Import from GitHub
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? 'Uploading…' : 'Upload EPUB'}
            </button>
          </div>
          <input ref={fileRef} type="file" accept=".epub" hidden onChange={onFile} />
        </div>

        {importOpen && (
          <ImportFromGithub
            githubConnected={!!viewer?.githubConnected}
            onClose={() => setImportOpen(false)}
          />
        )}

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        {isLoading && <p className="text-slate-400">Loading…</p>}

        {books && books.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">
            No books yet. Upload an EPUB to start translating.
          </div>
        )}

        <ul className="space-y-2">
          {books?.map((book) => {
            const ready = book.status === 'ready'
            const card = (
              <div
                className={`flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 ${
                  ready ? 'hover:border-slate-400' : 'opacity-70'
                }`}
              >
                <div>
                  <div className="font-medium text-slate-900">{book.title}</div>
                  <div className="text-xs text-slate-500">
                    {book.author && `${book.author} · `}
                    {book.sourceLanguage} → {book.targetLanguage}
                    {ready && ` · ${book.chaptersCount} chapters`}
                  </div>
                </div>
                <span className="text-xs text-slate-400">{STATUS_LABEL[book.status]}</span>
              </div>
            )
            return (
              <li key={book.slug} className="flex items-stretch gap-2">
                <div className="flex-1">
                  {ready ? <Link to={`/books/${book.slug}`}>{card}</Link> : card}
                </div>
                <button
                  onClick={() => setPreviewSlug(book.slug)}
                  className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 text-xs font-medium text-slate-500 hover:border-slate-400 hover:text-slate-800"
                  title="Preview git repo files"
                >
                  Repo
                </button>
              </li>
            )
          })}
        </ul>
      </main>

      {previewSlug && <RepoPreview slug={previewSlug} onClose={() => setPreviewSlug(null)} />}
    </div>
  )
}
